import React, { createContext, useContext, useEffect, useState } from "react";

type ColorTheme = "blue" | "green" | "purple" | "orange";

interface ColorThemeContextType {
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
}

const ColorThemeContext = createContext<ColorThemeContextType | undefined>(undefined);

export function useColorTheme() {
  const context = useContext(ColorThemeContext);
  if (context === undefined) {
    throw new Error("useColorTheme must be used within a ColorThemeProvider");
  }
  return context;
}

interface ColorThemeProviderProps {
  children: React.ReactNode;
}

export function ColorThemeProvider({ children }: ColorThemeProviderProps) {
  const [colorTheme, setColorThemeState] = useState<ColorTheme>("blue");

  useEffect(() => {
    // Load saved color theme from localStorage
    const savedTheme = localStorage.getItem("color-theme") as ColorTheme;
    if (savedTheme && ["blue", "green", "purple", "orange"].includes(savedTheme)) {
      setColorThemeState(savedTheme);
      applyColorTheme(savedTheme);
    }
  }, []);

  const applyColorTheme = (theme: ColorTheme) => {
    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList.remove("theme-green", "theme-purple", "theme-orange");
    
    // Apply new theme class (blue is default, no class needed)
    if (theme !== "blue") {
      root.classList.add(`theme-${theme}`);
    }
  };

  const setColorTheme = (theme: ColorTheme) => {
    setColorThemeState(theme);
    applyColorTheme(theme);
    localStorage.setItem("color-theme", theme);
  };

  return (
    <ColorThemeContext.Provider value={{ colorTheme, setColorTheme }}>
      {children}
    </ColorThemeContext.Provider>
  );
}